import { useInfiniteQuery, useQueryClient } from 'react-query';
import { QueryPageHookType } from '@ts/common.types';
import { useMemo, useCallback } from 'react';
import { OrganizationInviteResponseType } from '@ts/userOrganization.types';
import { getAllInvitesOfOrganizationApi } from '@api/userOrganization.api';

export default (
  all: boolean = false,
  enabled: boolean = true
): QueryPageHookType<OrganizationInviteResponseType> => {
  const queryClient = useQueryClient();
  const requestLimit = useMemo(() => (all ? 250 : 20), [all]);
  const key = useMemo(() => ['organization-invitations'], []);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery<OrganizationInviteResponseType[]>(
      key,
      ({ pageParam = 0 }) => getAllInvitesOfOrganizationApi(pageParam, requestLimit),
      {
        getNextPageParam: (lastPage: OrganizationInviteResponseType[], allPages) => {
          if (lastPage.length < requestLimit) return false;

          return allPages.length;
        },
        enabled,
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 15 * 60 * 1000,
      }
    );

  const removeFromCacheById = (id: number) =>
    queryClient.setQueryData(key, ({ pages, pageParams }) => {
      const updatePages = pages.map((page) =>
        page.filter((item: OrganizationInviteResponseType) => item.id !== id)
      );

      return {
        pageParams,
        pages: updatePages,
      };
    });

  const addToCache = (item: OrganizationInviteResponseType) =>
    queryClient.setQueryData(key, ({ pages, pageParams }) => {
      if (pages == null || pages.length === 0) {
        return {
          pageParams,
          pages: [[item]],
        };
      }

      const updatedPages = [...pages];
      updatedPages[updatedPages.length - 1] = [...updatedPages[updatedPages.length - 1], item];

      return {
        pageParams,
        pages: updatedPages,
      };
    });

  const updateInCache = (id: number, payload: OrganizationInviteResponseType) =>
    queryClient.setQueryData(key, ({ pages, pageParams }) => {
      const updatePages = pages.map((page) =>
        page.map((item: OrganizationInviteResponseType) => {
          if (item.id === id) return payload;
          return item;
        })
      );

      return {
        pageParams,
        pages: updatePages,
      };
    });

  const evictCacheByKey = useCallback(() => {
    queryClient.removeQueries(key);
  }, [queryClient, key]);

  return {
    items: data?.pages?.flat(),
    refetch,
    isLoading,
    isLoadingNextPage: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    removeItem: removeFromCacheById,
    addItem: addToCache,
    updateItem: updateInCache,
    evictByKey: evictCacheByKey,
  };
};
